import i18n from 'i18next';
import { getPathLocale, setPathLocale, stripPathLocale } from '../../common/localeShared';
import { gameConfig } from '../../gameConfig';
import { TRouter } from './router/types';
import { getTrackedInternalLinkUrl } from './tracking';

let router: TRouter | null = null;

const historySystem = {
  init(routerObj: TRouter) {
    router = routerObj;
  },
  push(str: string, options?: { state?: Record<string, unknown> }) {
    if (!router) {
      throw new Error('history not yet initialized');
    }
    router.navigate(setPathLocale(str, i18n.language), { state: options?.state });
  },

  replace(str: string, options?: { state?: Record<string, unknown> }) {
    if (!router) {
      throw new Error('history not yet initialized');
    }
    router.navigate(setPathLocale(str, i18n.language), { replace: true, state: options?.state });
  },

  listen(fn: any) {
    if (!router) {
      throw new Error('history not yet initialized');
    }

    router.subscribe((state) => {
      fn(state.location);
    });
  }
};

type TInternalNavigateToParams = {
  withPageRefresh: boolean;
  target: string;
};

function internalNavigateTo(key: string, domEvent?: any, params: Partial<TInternalNavigateToParams> = {}) {
  if (!history) {
    throw new Error('history not yet initialized');
  }

  const target = typeof params.target !== 'undefined' ? params.target : '_blank';

  const openInNew = domEvent && (domEvent.metaKey || domEvent.ctrlKey);
  if (key.indexOf('http') === 0) {
    if (openInNew) {
      window.open(key, target);
    } else {
      window.location.href = key;
    }
  } else if (openInNew) {
    window.open(setPathLocale(key, i18n.language), target);
  } else {
    if (params.withPageRefresh) {
      window.location.href = setPathLocale(key, i18n.language);
    } else {
      historySystem.push(key);
    }
  }
}

function getLocalizedSystemUri(to: string): string {
  if (stripPathLocale(to) !== '/logout') {
    return to;
  }

  const currentPathLocale = getPathLocale(window.location.pathname);
  return setPathLocale(to, currentPathLocale || i18n.language);
}

export function navigateTo(key: string, domEvent?: any) {
  internalNavigateTo(key, domEvent, { withPageRefresh: false });
}

export function navigateToWithPageRefresh(key: string, domEvent?: any) {
  internalNavigateTo(key, domEvent, { withPageRefresh: true });
}

export function navigateToSameWindow(key: string, domEvent?: any) {
  internalNavigateTo(key, domEvent);
}

const SYSTEM_URIS = ['/logout', '/admin', '/stop-impersonate'];

function parseTo(to: string) {
  const parser = document.createElement('a');
  parser.href = to;
  return parser;
}

// these need to be directed to with a standard link
function isSystemUri(to: string) {
  return SYSTEM_URIS.indexOf(stripPathLocale(to)) !== -1;
}

// internal need to be directed to with React Router Link,
// otherwise it's considered external and should open in
// new browser window
function isInternalUri(to: string) {
  if (stripPathLocale(to) === '/logout') {
    return false;
  }

  if (to.indexOf('://') === -1) {
    return true;
  }

  const toLocation = parseTo(to);
  return window.location.hostname === toLocation.hostname;
}

export function handleRCMenuOnClick(params: any, hash?: string) {
  const { key, domEvent } = params;
  const openInNew = domEvent && (domEvent.metaKey || domEvent.ctrlKey);
  const isInternal = isInternalUri(key);

  if (isInternal) {
    RIO_TRACK_EVENT('event', 'internal_click', {
      link_url: getTrackedInternalLinkUrl(key),
      link_text: params.domEvent.target.textContent
    });
  } else {
    RIO_TRACK_EVENT('event', 'click', {
      link_url: key,
      link_text: params.domEvent.target.textContent,
      outbound: 'true'
    });
  }

  if (openInNew) {
    window.open(key, '_blank');
  } else if (isSystemUri(key)) {
    window.location.href = getLocalizedSystemUri(key);
  } else if (isInternal) {
    if (hash) {
      historySystem.push(key + '#' + hash);
    } else {
      historySystem.push(key);
    }
  } else {
    window.open(key, '_blank');
  }
}

export function handleRCTitleMenuOnClick(destination: string | undefined, domEvent?: any) {
  if (!destination) {
    return undefined;
  }

  return navigateTo(destination, domEvent);
}

export const checkIfRouteExists = (path: string): boolean => {
  if (!gameConfig) {
    return false;
  }

  const withoutQueryParams = path.split('?')[0];
  const normalizedPath = withoutQueryParams.startsWith('/') ? withoutQueryParams : `/${withoutQueryParams}`;

  for (const route of Object.values(gameConfig.Routes)) {
    if (route === normalizedPath) {
      return true;
    }

    const routeParts = route.split('/');
    const pathParts = normalizedPath.split('/');

    let isMatch = true;

    for (let i = 0; i < routeParts.length; i++) {
      const routePart = routeParts[i];
      const pathPart = pathParts[i];

      if (routePart.startsWith(':')) {
        const isOptional = routePart.endsWith('?');

        if (!pathPart && !isOptional) {
          isMatch = false;
          break;
        }

        continue;
      }

      if (routePart !== pathPart) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      return true;
    }
  }

  return false;
};

export default historySystem;
